import { FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import {
  Bot,
  CheckCircle2,
  Clock3,
  Hotel,
  LogOut,
  MessageCircle,
  RefreshCw,
  Search,
  Send,
  ShieldCheck,
  UserRoundCheck,
  UsersRound,
  XCircle,
} from "lucide-react";
import type { Session } from "@supabase/supabase-js";
import { supabase } from "./lib/supabase";
import type {
  ChatMessage,
  Conversation,
  ConversationStatus,
  StaffProfile,
} from "./types";

const statusLabels: Record<ConversationStatus, string> = {
  new: "Novo",
  collecting: "Em coleta",
  ready_to_submit: "Dados completos",
  submitted: "Cotação enviada",
  human: "Atendimento humano",
  closed: "Encerrado",
};

const statusOrder: ConversationStatus[] = [
  "human",
  "new",
  "collecting",
  "ready_to_submit",
  "submitted",
  "closed",
];

function formatDateTime(value: string | null) {
  if (!value) return "Sem mensagens";
  return new Intl.DateTimeFormat("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value));
}

function formatDate(value?: string) {
  if (!value) return "—";
  return new Intl.DateTimeFormat("pt-BR", { timeZone: "UTC" }).format(
    new Date(`${value}T12:00:00Z`),
  );
}

function firstName(name?: string | null) {
  return name?.trim().split(/\s+/)[0] || "Hóspede";
}

function normalizeConversation(row: unknown): Conversation {
  const value = row as Conversation & {
    contact?: Conversation["contact"] | Conversation["contact"][];
    assignee?: Conversation["assignee"] | Conversation["assignee"][];
    quotes?: Conversation["quotes"];
  };

  return {
    ...value,
    contact: Array.isArray(value.contact) ? value.contact[0] : value.contact!,
    assignee: Array.isArray(value.assignee)
      ? value.assignee[0] ?? null
      : value.assignee ?? null,
    quotes: [...(value.quotes ?? [])].sort(
      (a, b) => +new Date(b.created_at) - +new Date(a.created_at),
    ),
  };
}

function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError("");
    const { error: signInError } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (signInError) setError("E-mail ou senha inválidos.");
    setBusy(false);
  }

  return (
    <main className="login-shell">
      <section className="login-brand">
        <div className="brand-mark"><Hotel size={30} /></div>
        <p className="eyebrow">Morro das Pedras Clube Hotel & SPA</p>
        <h1>Atendimento próximo, organizado e humano.</h1>
        <p>
          Acompanhe as conversas da Maria, assuma atendimentos e consulte cada
          solicitação de hospedagem em um só lugar.
        </p>
        <div className="login-trust">
          <ShieldCheck size={20} />
          <span>Acesso exclusivo para a equipe autorizada</span>
        </div>
      </section>

      <section className="login-card">
        <div>
          <p className="eyebrow">Central de atendimento</p>
          <h2>Entre na sua conta</h2>
          <p className="muted">Use o e-mail cadastrado pela administração.</p>
        </div>
        <form onSubmit={submit}>
          <label>
            E-mail
            <input
              autoComplete="email"
              onChange={(event) => setEmail(event.target.value)}
              placeholder="seuemail@hotel.com.br"
              required
              type="email"
              value={email}
            />
          </label>
          <label>
            Senha
            <input
              autoComplete="current-password"
              minLength={6}
              onChange={(event) => setPassword(event.target.value)}
              placeholder="••••••••"
              required
              type="password"
              value={password}
            />
          </label>
          {error && <p className="form-error">{error}</p>}
          <button className="primary-button full" disabled={busy} type="submit">
            {busy ? "Entrando…" : "Entrar"}
          </button>
        </form>
      </section>
    </main>
  );
}

function App() {
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<StaffProfile | null>(null);
  const [authReady, setAuthReady] = useState(false);
  const [accessError, setAccessError] = useState("");
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [filter, setFilter] = useState<"active" | "human" | "closed">("active");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [draft, setDraft] = useState("");
  const [notice, setNotice] = useState("");

  const selected = useMemo(
    () => conversations.find((item) => item.id === selectedId) ?? null,
    [conversations, selectedId],
  );

  const filtered = useMemo(() => {
    const query = search.trim().toLocaleLowerCase("pt-BR");
    return conversations
      .filter((item) => {
        if (filter === "human") return item.status === "human";
        if (filter === "closed") return item.status === "closed";
        return item.status !== "closed";
      })
      .filter((item) => {
        if (!query) return true;
        return (
          item.contact.full_name?.toLocaleLowerCase("pt-BR").includes(query) ||
          item.contact.phone_e164.includes(query)
        );
      })
      .sort((a, b) => {
        const statusDifference =
          statusOrder.indexOf(a.status) - statusOrder.indexOf(b.status);
        if (statusDifference !== 0) return statusDifference;
        return +(new Date(b.last_message_at ?? b.created_at)) -
          +(new Date(a.last_message_at ?? a.created_at));
      });
  }, [conversations, filter, search]);

  const loadConversations = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("conversations")
      .select(`
        id, status, agent_enabled, assigned_to, collected_data, missing_fields,
        last_message_at, created_at,
        contact:contacts!conversations_contact_id_fkey(
          id, full_name, phone_e164, email, language
        ),
        assignee:staff_profiles!conversations_assigned_to_fkey(
          id, display_name, role
        ),
        quotes:quote_requests!quote_requests_conversation_id_fkey(
          id, full_name, email, phone_e164, check_in, check_out, adults,
          children_0_6, children_7_plus, children_ages, notes, status, created_at
        )
      `)
      .order("last_message_at", { ascending: false, nullsFirst: false });

    if (error) {
      setNotice("Não foi possível carregar os atendimentos.");
    } else {
      const normalized = (data ?? []).map(normalizeConversation);
      setConversations(normalized);
      setSelectedId((current) =>
        current && normalized.some((item) => item.id === current)
          ? current
          : normalized[0]?.id ?? null,
      );
    }
    setLoading(false);
  }, []);

  const loadMessages = useCallback(async (conversationId: string) => {
    const { data, error } = await supabase
      .from("messages")
      .select(
        "id, direction, sender, message_type, body, delivery_status, occurred_at",
      )
      .eq("conversation_id", conversationId)
      .order("occurred_at", { ascending: true });

    if (!error) setMessages((data ?? []) as ChatMessage[]);
  }, []);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setAuthReady(true);
    });
    const { data } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      setSession(nextSession);
      setAuthReady(true);
    });
    return () => data.subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (!session) {
      setProfile(null);
      return;
    }
    supabase
      .from("staff_profiles")
      .select("id, display_name, role, is_active")
      .eq("id", session.user.id)
      .eq("is_active", true)
      .maybeSingle()
      .then(({ data, error }) => {
        if (error || !data) {
          setAccessError("Seu usuário ainda não foi liberado para atendimento.");
          return;
        }
        setAccessError("");
        setProfile(data as StaffProfile);
      });
  }, [session]);

  useEffect(() => {
    if (!profile) return;
    void loadConversations();
    const timer = window.setInterval(loadConversations, 12000);
    const channel = supabase
      .channel("maria-dashboard")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "conversations" },
        () => void loadConversations(),
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "messages" },
        () => {
          void loadConversations();
          if (selectedId) void loadMessages(selectedId);
        },
      )
      .subscribe();

    return () => {
      window.clearInterval(timer);
      void supabase.removeChannel(channel);
    };
  }, [loadConversations, loadMessages, profile, selectedId]);

  useEffect(() => {
    if (selectedId) void loadMessages(selectedId);
    else setMessages([]);
  }, [loadMessages, selectedId]);

  async function runAction(
    action: "take_over" | "return_to_agent" | "close" | "send_message",
    message?: string,
  ) {
    if (!selected) return;
    setActionBusy(true);
    setNotice("");
    const { data, error } = await supabase.functions.invoke("staff-console", {
      body: { action, conversation_id: selected.id, message },
    });

    if (error || !data?.success) {
      setNotice(data?.error ?? "Não foi possível concluir esta ação.");
    } else {
      if (action === "send_message") setDraft("");
      setNotice(
        action === "send_message"
          ? "Mensagem enviada."
          : "Atendimento atualizado.",
      );
      await Promise.all([
        loadConversations(),
        loadMessages(selected.id),
      ]);
    }
    setActionBusy(false);
  }

  async function sendMessage(event: FormEvent) {
    event.preventDefault();
    const message = draft.trim();
    if (!message) return;
    await runAction("send_message", message);
  }

  const activeCount = conversations.filter((item) => item.status !== "closed").length;
  const humanCount = conversations.filter((item) => item.status === "human").length;
  const readyCount = conversations.filter(
    (item) => item.status === "ready_to_submit" || item.status === "submitted",
  ).length;

  if (!authReady) {
    return <div className="center-state"><RefreshCw className="spin" /> Carregando…</div>;
  }
  if (!session) return <Login />;
  if (accessError) {
    return (
      <div className="center-state access-blocked">
        <ShieldCheck size={42} />
        <h2>Acesso aguardando liberação</h2>
        <p>{accessError}</p>
        <button className="secondary-button" onClick={() => supabase.auth.signOut()}>
          Sair
        </button>
      </div>
    );
  }
  if (!profile) {
    return <div className="center-state"><RefreshCw className="spin" /> Validando acesso…</div>;
  }

  return (
    <div className="app-shell">
      <header className="topbar">
        <div className="brand-inline">
          <span className="brand-mark small"><Hotel size={21} /></span>
          <span>
            <strong>Maria</strong>
            <small>Central de atendimento</small>
          </span>
        </div>
        <div className="topbar-actions">
          <span className="online-indicator"><i /> Maria online</span>
          <span className="staff-name">{profile.display_name}</span>
          <button
            aria-label="Sair"
            className="icon-button"
            onClick={() => supabase.auth.signOut()}
          >
            <LogOut size={19} />
          </button>
        </div>
      </header>

      <main className="dashboard">
        <section className="welcome-row">
          <div>
            <p className="eyebrow">Visão geral</p>
            <h1>Olá, {firstName(profile.display_name)}.</h1>
            <p className="muted">Acompanhe os hóspedes e entre na conversa quando necessário.</p>
          </div>
          <button
            className="secondary-button"
            disabled={loading}
            onClick={() => loadConversations()}
          >
            <RefreshCw className={loading ? "spin" : ""} size={17} />
            Atualizar
          </button>
        </section>

        <section className="metric-grid">
          <article><MessageCircle /><span><strong>{activeCount}</strong><small>Conversas ativas</small></span></article>
          <article><UserRoundCheck /><span><strong>{humanCount}</strong><small>Com atendente</small></span></article>
          <article><CheckCircle2 /><span><strong>{readyCount}</strong><small>Cotações processadas</small></span></article>
        </section>

        {notice && <div className="notice">{notice}</div>}

        <section className="workspace">
          <aside className="conversation-panel">
            <div className="panel-heading">
              <div>
                <p className="eyebrow">Caixa de entrada</p>
                <h2>Conversas</h2>
              </div>
              <span className="count-badge">{filtered.length}</span>
            </div>
            <div className="search-box">
              <Search size={17} />
              <input
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Nome ou telefone"
                value={search}
              />
            </div>
            <div className="filter-tabs">
              <button className={filter === "active" ? "active" : ""} onClick={() => setFilter("active")}>Ativas</button>
              <button className={filter === "human" ? "active" : ""} onClick={() => setFilter("human")}>Humanas</button>
              <button className={filter === "closed" ? "active" : ""} onClick={() => setFilter("closed")}>Encerradas</button>
            </div>
            <div className="conversation-list">
              {filtered.map((conversation) => (
                <button
                  className={conversation.id === selectedId ? "conversation-item selected" : "conversation-item"}
                  key={conversation.id}
                  onClick={() => setSelectedId(conversation.id)}
                >
                  <span className="avatar">{firstName(conversation.contact.full_name)[0]}</span>
                  <span className="conversation-copy">
                    <span>
                      <strong>{conversation.contact.full_name || "Novo contato"}</strong>
                      <time>{formatDateTime(conversation.last_message_at)}</time>
                    </span>
                    <small>{conversation.contact.phone_e164}</small>
                    <em className={`status-tag status-${conversation.status}`}>
                      {statusLabels[conversation.status]}
                    </em>
                  </span>
                </button>
              ))}
              {!filtered.length && (
                <div className="empty-list">
                  <MessageCircle size={28} />
                  <p>Nenhuma conversa neste filtro.</p>
                </div>
              )}
            </div>
          </aside>

          <section className="chat-panel">
            {selected ? (
              <>
                <header className="chat-header">
                  <div className="guest-heading">
                    <span className="avatar large">{firstName(selected.contact.full_name)[0]}</span>
                    <span>
                      <h2>{selected.contact.full_name || "Novo contato"}</h2>
                      <p>{selected.contact.phone_e164}</p>
                    </span>
                  </div>
                  <div className="chat-actions">
                    {selected.status !== "human" ? (
                      <button
                        className="primary-button"
                        disabled={actionBusy || selected.status === "closed"}
                        onClick={() => runAction("take_over")}
                      >
                        <UsersRound size={17} /> Assumir conversa
                      </button>
                    ) : (
                      <button
                        className="secondary-button"
                        disabled={actionBusy}
                        onClick={() => runAction("return_to_agent")}
                      >
                        <Bot size={17} /> Devolver à Maria
                      </button>
                    )}
                  </div>
                </header>

                <div className="message-area">
                  {messages.map((message) => (
                    <div
                      className={`message-row ${message.direction === "outbound" ? "outbound" : "inbound"}`}
                      key={message.id}
                    >
                      <div className={`message-bubble sender-${message.sender}`}>
                        <small>
                          {message.sender === "guest"
                            ? firstName(selected.contact.full_name)
                            : message.sender === "staff"
                              ? "Equipe"
                              : "Maria"}
                        </small>
                        <p>{message.body || `[${message.message_type}]`}</p>
                        <time>{formatDateTime(message.occurred_at)}</time>
                      </div>
                    </div>
                  ))}
                  {!messages.length && (
                    <div className="empty-chat">
                      <MessageCircle size={36} />
                      <p>O histórico aparecerá aqui.</p>
                    </div>
                  )}
                </div>

                <form className="composer" onSubmit={sendMessage}>
                  {selected.status !== "human" && (
                    <p><Bot size={15} /> Assuma a conversa para responder como atendente.</p>
                  )}
                  <div>
                    <textarea
                      disabled={selected.status !== "human" || actionBusy}
                      maxLength={1500}
                      onChange={(event) => setDraft(event.target.value)}
                      placeholder="Digite sua mensagem…"
                      rows={1}
                      value={draft}
                    />
                    <button
                      aria-label="Enviar mensagem"
                      className="send-button"
                      disabled={selected.status !== "human" || actionBusy || !draft.trim()}
                      type="submit"
                    >
                      <Send size={19} />
                    </button>
                  </div>
                </form>
              </>
            ) : (
              <div className="empty-chat full">
                <MessageCircle size={44} />
                <h2>Selecione uma conversa</h2>
                <p>Escolha um atendimento para ver o histórico completo.</p>
              </div>
            )}
          </section>

          <aside className="details-panel">
            {selected ? (
              <>
                <div className="panel-heading">
                  <div>
                    <p className="eyebrow">Detalhes</p>
                    <h2>Hóspede</h2>
                  </div>
                </div>
                <dl className="detail-list">
                  <div><dt>Nome</dt><dd>{selected.contact.full_name || "Não informado"}</dd></div>
                  <div><dt>Telefone</dt><dd>{selected.contact.phone_e164}</dd></div>
                  <div><dt>E-mail</dt><dd>{selected.contact.email || "Não informado"}</dd></div>
                  <div><dt>Idioma</dt><dd>{selected.contact.language}</dd></div>
                  <div><dt>Responsável</dt><dd>{selected.assignee?.display_name || "Maria"}</dd></div>
                </dl>

                <div className="section-divider" />

                <div className="quote-heading">
                  <div>
                    <p className="eyebrow">Solicitação</p>
                    <h2>Última cotação</h2>
                  </div>
                  {selected.quotes[0] && (
                    <span className={`quote-status quote-${selected.quotes[0].status}`}>
                      {selected.quotes[0].status}
                    </span>
                  )}
                </div>

                {selected.quotes[0] ? (
                  <dl className="quote-grid">
                    <div><dt>Check-in</dt><dd>{formatDate(selected.quotes[0].check_in)}</dd></div>
                    <div><dt>Check-out</dt><dd>{formatDate(selected.quotes[0].check_out)}</dd></div>
                    <div><dt>Adultos</dt><dd>{selected.quotes[0].adults}</dd></div>
                    <div><dt>0 a 6 anos</dt><dd>{selected.quotes[0].children_0_6}</dd></div>
                    <div><dt>7 anos ou mais</dt><dd>{selected.quotes[0].children_7_plus}</dd></div>
                    <div><dt>Idades</dt><dd>{selected.quotes[0].children_ages.join(", ") || "Não se aplica"}</dd></div>
                  </dl>
                ) : (
                  <div className="no-quote">
                    <Clock3 size={24} />
                    <p>A cotação ainda não foi concluída.</p>
                  </div>
                )}

                {selected.status !== "closed" && (
                  <button
                    className="danger-button"
                    disabled={actionBusy}
                    onClick={() => runAction("close")}
                  >
                    <XCircle size={17} /> Encerrar atendimento
                  </button>
                )}
              </>
            ) : (
              <div className="empty-details">Os dados do hóspede aparecerão aqui.</div>
            )}
          </aside>
        </section>
      </main>
    </div>
  );
}

export default App;
